import { DB_NAME } from './constants';

let DB;

/**
 * Connects to the IndexedDB.
 *
 * @param {Object} options - The options for connecting to the IndexedDB.
 * @param {number} [options.version=1] - The version of the database.
 * @param {Object} [options.tables] - The tables to create in the database.
 * @returns {Promise<IDBDatabase>} A promise that resolves with the database connection.
 */
export function connectIndexedDB(
  options = {
    version: 1,
    tables: {},
  }
) {
  return new Promise((resolve, reject) => {
    if (DB) {
      resolve(DB);

      if (options.tables) {
        for (const [tableName, tableOptions] of Object.entries(options.tables)) {
          createTable(tableName, tableOptions);
        }
      }
    }

    const request = indexedDB.open(DB_NAME, options.version || 1);

    request.onerror = (event) => {
      reject(event);
    };

    request.onsuccess = (event) => {
      DB = event.target.result;
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      if (!DB) {
        DB = event.target.result;
      }

      if (options.tables) {
        for (const [tableName, tableOptions] of Object.entries(options.tables)) {
          createTable(tableName, tableOptions);
        }
      }
    };
  });
}

export const createTable = (tableName, tableOptions) => {
  if (!DB.objectStoreNames.contains(tableName)) {
    const objectStore = DB.createObjectStore(tableName, tableOptions);
    if (tableOptions.indexes) {
      for (const index of tableOptions.indexes) {
        objectStore.createIndex(index.name, index.keyPath, index.options);
      }
    }
  }
};

/**
 * Creates a repository for the specified table.
 *
 * @param {string} tableName - The name of the table to create the repository for.
 */
/**
 * Deeply compares two objects.
 *
 * @param {Object} obj1 - The first object to compare.
 * @param {Object} obj2 - The second object to compare.
 * @returns {boolean} True if the objects are equal, false otherwise.
 */
function deepCompare(obj1, obj2) {
  if (obj1 === obj2) return true;

  if (typeof obj1 !== 'object' || typeof obj2 !== 'object' || obj1 === null || obj2 === null) {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  for (const key of keys1) {
    if (!keys2.includes(key) || !deepCompare(obj1[key], obj2[key])) {
      return false;
    }
  }

  return true;
}

export const createRepository = (tableName) => {
  return {
    find(filter = {}) {
      return new Promise((resolve, reject) => {
        const transaction = DB.transaction([tableName], 'readonly');
        const objectStore = transaction.objectStore(tableName);
        const records = [];
        const request = objectStore.openCursor();

        request.onsuccess = (event) => {
          const cursor = event.target.result;
          if (cursor) {
            const record = cursor.value;
            if (Object.entries(filter).every(([key, value]) => deepCompare(record[key], value))) {
              records.push(record);
            }

            cursor.continue();
          } else {
            resolve(records);
          }
        };

        request.onerror = (event) => {
          reject(event.target.error);
        };
      });
    },

    /**
     * Adds a new record to the specified table.
     *
     * @param {Object} data - The data to add to the table.
     * @returns {Promise<number>} A promise that resolves with the ID of the added record.
     */
    add(data) {
      return new Promise((resolve, reject) => {
        const transaction = DB.transaction([tableName], 'readwrite');
        const objectStore = transaction.objectStore(tableName);
        const request = objectStore.add(data);

        request.onsuccess = (event) => {
          resolve(event.target.result);
        };
        request.onerror = (event) => {
          reject(event.target.error);
        };
      });
    },
    /**
     * Updates an existing record in the specified table.
     *
     * @param {number} id - The ID of the record to update.
     * @param {Object} payload - The data to update in the record.
     * @returns {Promise<number>} A promise that resolves with the ID of the updated record.
     */
    update(id, payload) {
      return new Promise((resolve, reject) => {
        const transaction = DB.transaction([tableName], 'readwrite');
        const objectStore = transaction.objectStore(tableName);
        const request = objectStore.get(id);

        request.onsuccess = (event) => {
          const data = event.target.result;

          Object.assign(data, payload);

          const updateRequest = objectStore.put(data);

          updateRequest.onsuccess = (event) => {
            resolve(event.target.result);
          };
          updateRequest.onerror = (event) => {
            reject(event.target.error);
          };
        };
        request.onerror = (event) => {
          reject(event.target.error);
        };
      });
    },
    /**
     * Deletes a record from the specified table.
     *
     * @param {number} id - The ID of the record to delete.
     * @returns {Promise<void>} A promise that resolves when the record is deleted.
     */
    delete(id) {
      return new Promise((resolve, reject) => {
        const transaction = DB.transaction([tableName], 'readwrite');
        const objectStore = transaction.objectStore(tableName);
        const request = objectStore.delete(id);
        request.onsuccess = (event) => {
          resolve(event.target.result);
        };
        request.onerror = (event) => {
          reject(event.target.error);
        };
      });
    },
  };
};
